import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Card, Button, Alert, Spinner, Form,
  Row, Col, ProgressBar, Badge, ListGroup, Modal
} from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaCheck, FaArrowLeft, FaSave, FaUndo, FaCalendarAlt, 
  FaFileUpload, FaTrash, FaDownload, FaPlus, FaTasks,
  FaPaperclip, FaEdit, FaHistory, FaExclamationTriangle,
  FaCheckCircle, FaComments
} from 'react-icons/fa';
import MainLayout from '../../layouts/MainLayout';
import mockRnDService from '../../services/mockRnDService';
import { format } from 'date-fns';
import Chart from 'react-apexcharts';
import './ProjectProgress.css';

// Helper component for file uploads
const FileUploader = ({ onFileSelect }) => {
  const fileInputRef = useRef(null);
  
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      onFileSelect(e.target.files[0]);
    }
  };

  return (
    <div className="file-uploader">
      <Button 
        variant="outline-primary" 
        onClick={() => fileInputRef.current.click()}
        className="d-flex align-items-center"
      >
        <FaFileUpload className="me-2" /> Select File
      </Button>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleFileChange}
      />
    </div>
  );
};

// Comment component
const CommentSection = ({ comments, onAddComment, projectId }) => {
  const [newComment, setNewComment] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(newComment);
      setNewComment('');
    }
  };
  
  return (
    <div className="comment-section">
      <h6 className="mb-3">Discussion ({comments.length})</h6>
      
      <Form onSubmit={handleSubmit} className="mb-3">
        <Form.Group className="mb-2">
          <Form.Control 
            as="textarea" 
            rows={2} 
            placeholder="Add a comment or note..." 
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
        </Form.Group>
        <div className="text-end">
          <Button 
            type="submit" 
            variant="primary" 
            size="sm" 
            disabled={!newComment.trim()}
          >
            <FaComments className="me-1" /> Add Comment
          </Button>
        </div>
      </Form>
      
      <ListGroup variant="flush" className="comment-list">
        {comments.length > 0 ? (
          comments.map((comment, index) => (
            <ListGroup.Item key={index} className="px-0">
              <div className="d-flex justify-content-between">
                <strong>{comment.author}</strong>
                <small className="text-muted">{format(new Date(comment.timestamp), 'MMM d, yyyy HH:mm')}</small>
              </div>
              <p className="mb-0">{comment.text}</p>
            </ListGroup.Item>
          ))
        ) : (
          <p className="text-muted">No comments yet</p>
        )}
      </ListGroup>
    </div>
  );
};

// Milestone component 
const MilestoneManager = ({ milestones, onMilestoneUpdate, onAddMilestone }) => {
  const [editIndex, setEditIndex] = useState(-1);
  const [newMilestone, setNewMilestone] = useState({ 
    name: '', 
    dueDate: '', 
    completed: false 
  });
  const [showAddForm, setShowAddForm] = useState(false);

  const handleEdit = (index) => {
    setEditIndex(index);
  };

  const handleSave = (index, milestone) => {
    onMilestoneUpdate(index, milestone);
    setEditIndex(-1);
  };

  const handleAddMilestone = () => {
    if (newMilestone.name && newMilestone.dueDate) {
      onAddMilestone(newMilestone);
      setNewMilestone({ name: '', dueDate: '', completed: false });
      setShowAddForm(false);
    }
  };

  return (
    <div className="milestone-manager">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Project Milestones</h6>
        <Button 
          variant="outline-success" 
          size="sm" 
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <FaPlus className="me-1" /> Add Milestone
        </Button>
      </div>

      {showAddForm && (
        <Card className="mb-3">
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Milestone Name</Form.Label>
              <Form.Control
                type="text"
                value={newMilestone.name}
                onChange={(e) => setNewMilestone({...newMilestone, name: e.target.value})}
                placeholder="Enter milestone name"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                value={newMilestone.dueDate}
                onChange={(e) => setNewMilestone({...newMilestone, dueDate: e.target.value})}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="success" 
                size="sm" 
                onClick={handleAddMilestone}
                disabled={!newMilestone.name || !newMilestone.dueDate}
              >
                Save Milestone
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {milestones.length > 0 ? (
        <ListGroup>
          {milestones.map((milestone, index) => (
            <ListGroup.Item key={index} className={milestone.completed ? 'bg-light' : ''}>
              {editIndex === index ? (
                <div>
                  <Form.Group className="mb-2">
                    <Form.Label>Milestone Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={milestone.name}
                      onChange={(e) => 
                        onMilestoneUpdate(index, { ...milestone, name: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={milestone.dueDate}
                      onChange={(e) => 
                        onMilestoneUpdate(index, { ...milestone, dueDate: e.target.value })}
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end gap-2">
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      onClick={() => setEditIndex(-1)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      size="sm" 
                      onClick={() => handleSave(index, milestone)}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Form.Check
                      type="checkbox"
                      checked={milestone.completed}
                      onChange={(e) => 
                        onMilestoneUpdate(index, { ...milestone, completed: e.target.checked })}
                      label={
                        <span className={milestone.completed ? 'text-decoration-line-through' : ''}>
                          {milestone.name}
                        </span>
                      }
                      id={`milestone-check-${index}`}
                    />
                    <small className="text-muted d-block">
                      Due: {format(new Date(milestone.dueDate), 'MMM d, yyyy')}
                    </small>
                  </div>
                  <div>
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => handleEdit(index)}
                      className="p-0 me-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button 
                      variant="link" 
                      className="text-danger p-0" 
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this milestone?')) {
                          onMilestoneUpdate(index, null); // null means delete
                        }
                      }}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <Alert variant="light">
          No milestones created yet. Add some milestones to track project progress.
        </Alert>
      )}
    </div>
  );
};

// Main component
const ProjectProgress = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('progress');
  const [showAddAttachmentModal, setShowAddAttachmentModal] = useState(false);
  const [newAttachment, setNewAttachment] = useState({ file: null, description: '' });
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', assignee: '', dueDate: '', status: 'pending', description: '' });
  
  // Base progress states
  const [progress, setProgress] = useState({
    research: false,
    production: false,
    photo: false,
    video: false,
    finished: false
  });

  // Extended progress tracking features
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [progressHistory, setProgressHistory] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState([]);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const data = await mockRnDService.getProject(parseInt(id));
        setProject(data);
        
        // Initialize progress state from project data
        if (data.progress) {
          setProgress({
            research: data.progress.research || false,
            production: data.progress.production || false,
            photo: data.progress.photo || false,
            video: data.progress.video || false,
            finished: data.progress.finished || false
          });
        }

        // Load extended tracking data
        if (data.milestones) setMilestones(data.milestones);
        if (data.tasks) setTasks(data.tasks);
        if (data.attachments) setAttachments(data.attachments);
        if (data.comments) setComments(data.comments);
        if (data.progressHistory) setProgressHistory(data.progressHistory);
        if (data.riskAlerts) setRiskAlerts(data.riskAlerts);

        // If no milestone exists yet, create default ones based on project stages
        if (!data.milestones || data.milestones.length === 0) {
          const defaultMilestones = [
            {
              name: 'Research Phase Completion',
              dueDate: data.start_date, // Just a placeholder, should be adjusted
              completed: data.progress?.research || false
            },
            {
              name: 'Production Phase Completion',
              dueDate: data.target_date, // Just a placeholder
              completed: data.progress?.production || false
            }
          ];
          setMilestones(defaultMilestones);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Gagal memuat data proyek. ' + err.message);
        toast.error('Gagal memuat data proyek');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  const handleProgressChange = (stage, value) => {
    setProgress(prev => {
      const newProgress = { ...prev, [stage]: value };
      
      // If marking a stage as incomplete, also mark all dependent stages as incomplete
      if (!value) {
        switch (stage) {
          case 'research':
            return {
              ...newProgress,
              production: false,
              photo: false,
              video: false,
              finished: false
            };
          case 'production':
            return {
              ...newProgress,
              photo: false,
              video: false,
              finished: false
            };
          case 'photo':
            return {
              ...newProgress,
              video: false,
              finished: false
            };
          case 'video':
            return {
              ...newProgress,
              finished: false
            };
          default:
            return newProgress;
        }
      }
      
      return newProgress;
    });
  };

  const calculateProgressPercentage = () => {
    const stagesCount = Object.keys(progress).length;
    const completedCount = Object.values(progress).filter(Boolean).length;
    return Math.round((completedCount / stagesCount) * 100);
  };

  const handleAddMilestone = (newMilestone) => {
    setMilestones([...milestones, newMilestone]);
  };

  const handleMilestoneUpdate = (index, updatedMilestone) => {
    if (updatedMilestone === null) {
      // Delete milestone
      const updatedMilestones = [...milestones];
      updatedMilestones.splice(index, 1);
      setMilestones(updatedMilestones);
    } else {
      // Update milestone
      const updatedMilestones = [...milestones];
      updatedMilestones[index] = updatedMilestone;
      setMilestones(updatedMilestones);
    }
  };

  const handleAddComment = (text) => {
    const newComment = {
      text,
      author: localStorage.getItem('username') || 'User',
      timestamp: new Date().toISOString()
    };
    setComments([...comments, newComment]);
  };

  const handleAddTask = () => {
    if (newTask.title && newTask.dueDate) {
      const taskWithId = {
        ...newTask,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        createdBy: localStorage.getItem('username') || 'User'
      };
      
      setTasks([...tasks, taskWithId]);
      setNewTask({ title: '', assignee: '', dueDate: '', status: 'pending', description: '' });
      setShowAddTaskModal(false);
      toast.success('Task added successfully');
    }
  };

  const handleTaskStatusChange = (taskId, newStatus) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, status: newStatus } : task
    );
    setTasks(updatedTasks);
  };

  const handleAddAttachment = () => {
    if (newAttachment.file) {
      // In a real app, you would upload the file to a server here
      // For mock, we'll just store file metadata
      const attachment = {
        id: Date.now().toString(),
        name: newAttachment.file.name,
        description: newAttachment.description,
        size: newAttachment.file.size,
        type: newAttachment.file.type,
        uploadedAt: new Date().toISOString(),
        uploadedBy: localStorage.getItem('username') || 'User',
        url: URL.createObjectURL(newAttachment.file) // For demo purposes
      };
      
      setAttachments([...attachments, attachment]);
      setNewAttachment({ file: null, description: '' });
      setShowAddAttachmentModal(false);
      toast.success('Attachment added successfully');
    }
  };

  const handleDeleteAttachment = (attachmentId) => {
    if (window.confirm('Are you sure you want to delete this attachment?')) {
      const updatedAttachments = attachments.filter(a => a.id !== attachmentId);
      setAttachments(updatedAttachments);
      toast.success('Attachment deleted');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Record the progress history entry
      const historyEntry = {
        date: new Date().toISOString(),
        user: localStorage.getItem('username') || 'User',
        changes: {
          progress: {...progress},
          milestones: [...milestones],
          tasks: [...tasks]
        }
      };
      
      const updatedHistory = [...progressHistory, historyEntry];
      
      // Call the API to update both basic progress and extended data
      const projectData = {
        progress,
        milestones,
        tasks,
        attachments,
        comments,
        progressHistory: updatedHistory,
        riskAlerts
      };
      
      const updatedProject = await mockRnDService.updateProjectProgress(id, projectData);
      
      if (progress.finished) {
        toast.success('Proyek berhasil diselesaikan!');
        navigate('/dashboard/rnd'); // Redirect to the dashboard after completion
      } else {
        setProject(updatedProject);
        setProgressHistory(updatedHistory);
        toast.success('Progress berhasil diperbarui!');
      }
    } catch (err) {
      console.error('Error saving progress:', err);
      toast.error('Gagal menyimpan progress: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset progress to original values from project
    if (project && project.progress) {
      setProgress({
        research: project.progress.research || false,
        production: project.progress.production || false,
        photo: project.progress.photo || false,
        video: project.progress.video || false,
        finished: project.progress.finished || false
      });
      
      // Also reset milestones to match current progress
      const updatedMilestones = milestones.map(m => {
        if (m.name.includes('Research')) {
          return { ...m, completed: project.progress.research || false };
        } else if (m.name.includes('Production')) {
          return { ...m, completed: project.progress.production || false };
        } else if (m.name.includes('Photo')) {
          return { ...m, completed: project.progress.photo || false };
        } else if (m.name.includes('Video')) {
          return { ...m, completed: project.progress.video || false };
        }
        return m;
      });
      
      setMilestones(updatedMilestones);
    }
  };

  // Generate chart data for progress visualization
  const generateProgressChartData = () => {
    return {
      options: {
        chart: {
          id: 'project-progress-chart',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 225,
            hollow: {
              margin: 0,
              size: '70%',
              background: '#fff',
              dropShadow: {
                enabled: true,
                top: 3,
                left: 0,
                blur: 4,
                opacity: 0.24
              }
            },
            track: {
              background: '#fff',
              strokeWidth: '67%',
              margin: 0,
              dropShadow: {
                enabled: true,
                top: -3,
                left: 0,
                blur: 4,
                opacity: 0.35
              }
            },
            dataLabels: {
              show: true,
              name: {
                offsetY: -10,
                show: true,
                color: '#888',
                fontSize: '17px'
              },
              value: {
                formatter: function(val) {
                  return parseInt(val) + '%';
                },
                color: '#111',
                fontSize: '36px',
                show: true,
              }
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: ['#ABE5A1'],
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100]
          }
        },
        stroke: {
          lineCap: 'round'
        },
        labels: ['Progress']
      },
      series: [calculateProgressPercentage()]
    };
  };

  // Generate milestone chart data
  const generateMilestoneChartData = () => {
    const completedMilestones = milestones.filter(m => m.completed).length;
    const percentComplete = milestones.length ? 
      Math.round((completedMilestones / milestones.length) * 100) : 0;
    
    return {
      options: {
        chart: {
          id: 'milestone-chart',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: '70%',
            }
          }
        },
        colors: ['#20c997'],
        labels: ['Milestones']
      },
      series: [percentComplete]
    };
  };

  // Generate tasks summary chart
  const generateTasksChartData = () => {
    const statuses = tasks.reduce((acc, task) => {
      acc[task.status] = (acc[task.status] || 0) + 1;
      return acc;
    }, {});
    
    return {
      options: {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: ['Completed', 'In Progress', 'Pending'],
        colors: ['#28a745', '#17a2b8', '#ffc107'],
        legend: {
          position: 'bottom'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      series: [
        statuses.completed || 0,
        statuses.inprogress || 0,
        statuses.pending || 0
      ]
    };
  };

  if (loading) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="mt-3">Memuat data proyek...</p>
          </div>
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <Alert variant="danger">
            {error}
          </Alert>
          <Button
            variant="primary"
            as={Link}
            to="/rnd/projects"
          >
            <FaArrowLeft className="me-2" />
            Kembali ke Daftar Proyek
          </Button>
        </Container>
      </MainLayout>
    );
  }

  if (!project) {
    return (
      <MainLayout>
        <Container fluid className="py-4">
          <Alert variant="warning">
            Proyek tidak ditemukan
          </Alert>
          <Button
            variant="primary"
            as={Link}
            to="/rnd/projects"
          >
            <FaArrowLeft className="me-2" />
            Kembali ke Daftar Proyek
          </Button>
        </Container>
      </MainLayout>
    );
  }

  const progressChart = generateProgressChartData();
  const milestoneChart = generateMilestoneChartData();
  const tasksChart = generateTasksChartData();

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center bg-white">
            <h4 className="mb-0">
              Update Progress: {project.name}
            </h4>
            <div>
              <Button
                variant="outline-secondary"
                as={Link}
                to={`/rnd/projects/${id}`}
                className="me-2"
              >
                <FaArrowLeft className="me-1" /> Detail Proyek
              </Button>
              
              <Button
                variant="outline-danger"
                className="me-2"
                onClick={handleReset}
              >
                <FaUndo className="me-1" /> Reset
              </Button>
              
              <Button
                variant="success"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" /> Simpan Progress
                  </>
                )}
              </Button>
            </div>
          </Card.Header>
          
          <Card.Body>
            {/* Progress Summary Card */}
            <Row className="mb-4">
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <Row>
                      <Col md={8}>
                        <h5 className="mb-3">{project.name}</h5>
                        <p className="text-muted">{project.description}</p>
                        <div className="d-flex mb-3 gap-4">
                          <div>
                            <small className="text-muted d-block">Start Date</small>
                            <span>{project.start_date}</span>
                          </div>
                          <div>
                            <small className="text-muted d-block">Target Completion</small>
                            <span>{project.target_date}</span>
                          </div>
                          <div>
                            <small className="text-muted d-block">Manager</small>
                            <span>{project.manager || 'Not assigned'}</span>
                          </div>
                          <div>
                            <small className="text-muted d-block">Status</small>
                            <Badge bg={project.status === 'finished' ? 'success' : 'primary'}>
                              {project.status === 'finished' ? 'Completed' : 'Active'}
                            </Badge>
                          </div>
                        </div>
                        
                        <h6 className="mb-2">Overall Progress</h6>
                        <ProgressBar 
                          now={calculateProgressPercentage()} 
                          label={`${calculateProgressPercentage()}%`}
                          variant="success"
                          style={{ height: '10px' }}
                          className="mb-3"
                        />
                      </Col>
                      
                      <Col md={4} className="text-center">
                        <Chart 
                          options={progressChart.options}
                          series={progressChart.series}
                          type="radialBar"
                          height={230}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Custom Tabs Navigation - Improved Horizontal Layout */}
            <div className="tabs-container">
              <div id="progress-tracking-tabs" className="nav nav-tabs">
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'progress' ? 'active' : ''}`}
                    onClick={() => setActiveTab('progress')}
                    type="button"
                  >
                    Progress Stages
                  </button>
                </div>
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'milestones' ? 'active' : ''}`}
                    onClick={() => setActiveTab('milestones')}
                    type="button"
                  >
                    Milestones
                  </button>
                </div>
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'tasks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tasks')}
                    type="button"
                  >
                    Tasks
                  </button>
                </div>
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'files' ? 'active' : ''}`}
                    onClick={() => setActiveTab('files')}
                    type="button"
                  >
                    Files &amp; Attachments
                  </button>
                </div>
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'comments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('comments')}
                    type="button"
                  >
                    Comments
                  </button>
                </div>
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                    type="button"
                  >
                    History
                  </button>
                </div>
                <div className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'risks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('risks')}
                    type="button"
                  >
                    Risks
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'progress' && (
                <Card className="mb-3">
                  {/* Progress tab content */}
                  <Card.Header className="bg-white">
                    <h5 className="mb-0">Update Status Tahapan</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form>
                      {/* Research stage */}
                      <div className="mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Tahap Penelitian</h6>
                          <Badge bg={progress.research ? 'success' : 'secondary'}>
                            {progress.research ? 'Selesai' : 'Pending'}
                          </Badge>
                        </div>
                        <Form.Check 
                          type="switch"
                          id="custom-switch-research"
                          label="Tandai sebagai selesai"
                          checked={progress.research}
                          onChange={(e) => handleProgressChange('research', e.target.checked)}
                        />
                      </div>

                      {/* Production stage */}
                      <div className="mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Tahap Produksi</h6>
                          <Badge bg={progress.production ? 'success' : progress.research ? 'primary' : 'secondary'}>
                            {progress.production ? 'Selesai' : progress.research ? 'Aktif' : 'Pending'}
                          </Badge>
                        </div>
                        <Form.Check 
                          type="switch"
                          id="custom-switch-production"
                          label="Tandai sebagai selesai"
                          checked={progress.production}
                          onChange={(e) => handleProgressChange('production', e.target.checked)}
                          disabled={!progress.research}
                        />
                      </div>

                      {/* Photo stage */}
                      <div className="mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Tahap Foto</h6>
                          <Badge bg={progress.photo ? 'success' : progress.production ? 'primary' : 'secondary'}>
                            {progress.photo ? 'Selesai' : progress.production ? 'Aktif' : 'Pending'}
                          </Badge>
                        </div>
                        <Form.Check 
                          type="switch"
                          id="custom-switch-photo"
                          label="Tandai sebagai selesai"
                          checked={progress.photo}
                          onChange={(e) => handleProgressChange('photo', e.target.checked)}
                          disabled={!progress.production}
                        />
                      </div>

                      {/* Video stage */}
                      <div className="mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Tahap Video</h6>
                          <Badge bg={progress.video ? 'success' : progress.photo ? 'primary' : 'secondary'}>
                            {progress.video ? 'Selesai' : progress.photo ? 'Aktif' : 'Pending'}
                          </Badge>
                        </div>
                        <Form.Check 
                          type="switch"
                          id="custom-switch-video"
                          label="Tandai sebagai selesai"
                          checked={progress.video}
                          onChange={(e) => handleProgressChange('video', e.target.checked)}
                          disabled={!progress.photo}
                        />
                      </div>

                      {/* Final completion */}
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Penyelesaian Proyek</h6>
                          <Badge bg={progress.finished ? 'success' : progress.video ? 'primary' : 'secondary'}>
                            {progress.finished ? 'Selesai' : progress.video ? 'Aktif' : 'Pending'}
                          </Badge>
                        </div>
                        <Form.Check 
                          type="switch"
                          id="custom-switch-finished"
                          label="Tandai proyek sebagai selesai"
                          checked={progress.finished}
                          onChange={(e) => handleProgressChange('finished', e.target.checked)}
                          disabled={!progress.video}
                        />
                        {progress.video && !progress.finished && (
                          <Alert variant="info" className="mt-2">
                            <small>
                              All project stages are complete. Mark the project as finished to complete it.
                            </small>
                          </Alert>
                        )}
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              )}
              
              {activeTab === 'milestones' && (
                <Row>
                  <Col lg={8}>
                    <Card>
                      <Card.Body>
                        <MilestoneManager
                          milestones={milestones}
                          onMilestoneUpdate={handleMilestoneUpdate}
                          onAddMilestone={handleAddMilestone}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card>
                      <Card.Body className="text-center">
                        <h6>Milestone Completion</h6>
                        <Chart 
                          options={milestoneChart.options}
                          series={milestoneChart.series}
                          type="radialBar"
                          height={250}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
              
              {activeTab === 'tasks' && (
                <Row>
                  <Col lg={8}>
                    <Card>
                      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Project Tasks</h5>
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={() => setShowAddTaskModal(true)}
                        >
                          <FaPlus className="me-1" /> Add Task
                        </Button>
                      </Card.Header>
                      <Card.Body>
                        {tasks.length > 0 ? (
                          <ListGroup className="mb-3">
                            {tasks.map((task) => (
                              <ListGroup.Item key={task.id} className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div className="mb-1">
                                    <span className={task.status === 'completed' ? 'text-decoration-line-through' : ''}>
                                      {task.title}
                                    </span>
                                    <Badge 
                                      bg={
                                        task.status === 'completed' ? 'success' : 
                                        task.status === 'inprogress' ? 'info' : 'warning'
                                      }
                                      className="ms-2"
                                    >
                                      {task.status === 'completed' ? 'Completed' : 
                                       task.status === 'inprogress' ? 'In Progress' : 'Pending'}
                                    </Badge>
                                  </div>
                                  <div className="text-muted small">
                                    {task.assignee && <span className="me-2">Assigned to: {task.assignee}</span>}
                                    <span>Due: {task.dueDate}</span>
                                  </div>
                                </div>
                                <div className="d-flex">
                                  <Form.Select 
                                    size="sm" 
                                    value={task.status}
                                    onChange={(e) => handleTaskStatusChange(task.id, e.target.value)}
                                    style={{ width: '140px' }}
                                    className="me-2"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="inprogress">In Progress</option>
                                    <option value="completed">Completed</option>
                                  </Form.Select>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => {
                                      if (window.confirm('Are you sure you want to delete this task?')) {
                                        const updatedTasks = tasks.filter(t => t.id !== task.id);
                                        setTasks(updatedTasks);
                                      }
                                    }}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <Alert variant="light" className="text-center">
                            No tasks have been created for this project.
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4}>
                    <Card>
                      <Card.Body className="text-center">
                        <h6>Tasks Overview</h6>
                        {tasks.length > 0 ? (
                          <Chart 
                            options={tasksChart.options}
                            series={tasksChart.series}
                            type="donut"
                            height={250}
                          />
                        ) : (
                          <div className="py-5 text-muted">
                            <FaTasks size={40} className="mb-3" />
                            <p>No tasks data available</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
              
              {activeTab === 'files' && (
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Project Files</h5>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => setShowAddAttachmentModal(true)}
                    >
                      <FaFileUpload className="me-1" /> Upload File
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <ListGroup>
                      {attachments.length > 0 ? (
                        attachments.map((attachment) => (
                          <ListGroup.Item key={attachment.id} className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{attachment.name}</strong>
                              <div className="text-muted small">
                                {attachment.description}
                              </div>
                              <div className="text-muted small">
                                Uploaded by {attachment.uploadedBy} on {format(new Date(attachment.uploadedAt), 'MMM d, yyyy')}
                              </div>
                            </div>
                            <div>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => {
                                  // In a real app, this would download the file
                                  // For demo, we'll just open the URL
                                  if (attachment.url) {
                                    window.open(attachment.url);
                                  }
                                }}
                              >
                                <FaDownload className="me-1" /> Download
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))
                      ) : (
                        <Alert variant="light" className="text-center">
                          No files have been uploaded for this project.
                        </Alert>
                      )}
                    </ListGroup>
                  </Card.Body>
                </Card>
              )}
              
              {activeTab === 'comments' && (
                <Card>
                  <Card.Body>
                    <CommentSection 
                      comments={comments}
                      onAddComment={handleAddComment}
                      projectId={id}
                    />
                  </Card.Body>
                </Card>
              )}
              
              {activeTab === 'history' && (
                <Card>
                  <Card.Body>
                    <h5 className="mb-3">Progress History</h5>
                    {progressHistory.length > 0 ? (
                      <ListGroup variant="flush">
                        {progressHistory.map((entry, index) => (
                          <ListGroup.Item key={index} className="border-bottom">
                            <div className="d-flex justify-content-between mb-2">
                              <strong>{format(new Date(entry.date), 'MMMM d, yyyy HH:mm')}</strong>
                              <span>Updated by: {entry.user}</span>
                            </div>
                            <div>
                              {entry.changes.progress && (
                                <div className="mb-2">
                                  <h6>Progress Updates:</h6>
                                  <ul className="mb-0 list-unstyled ps-3">
                                    {Object.entries(entry.changes.progress).map(([key, value]) => (
                                      <li key={key}>
                                        {key}: {value ? '✅ Completed' : '⏳ Pending'}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <Alert variant="light">
                        No history records available.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}
              
              {activeTab === 'risks' && (
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Risk Management</h5>
                      <Button 
                        variant="warning" 
                        size="sm"
                        onClick={() => {
                          const newAlert = {
                            id: Date.now().toString(),
                            title: 'New Risk Alert',
                            description: 'Describe the risk here...',
                            priority: 'medium',
                            status: 'open',
                            createdAt: new Date().toISOString(),
                            createdBy: localStorage.getItem('username') || 'User'
                          };
                          setRiskAlerts([...riskAlerts, newAlert]);
                        }}
                      >
                        <FaExclamationTriangle className="me-1" /> Add Risk Alert
                      </Button>
                    </div>

                    {riskAlerts.length > 0 ? (
                      <ListGroup>
                        {riskAlerts.map(alert => (
                          <ListGroup.Item key={alert.id} className="border-start border-5 border-warning">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="mb-1">{alert.title}</h6>
                                <p className="mb-1">{alert.description}</p>
                                <small className="text-muted">
                                  Created by {alert.createdBy} on {format(new Date(alert.createdAt), 'MMM d, yyyy')}
                                </small>
                              </div>
                              <Badge 
                                bg={alert.priority === 'high' ? 'danger' : 
                                    alert.priority === 'medium' ? 'warning' : 'info'}
                              >
                                {alert.priority} priority
                              </Badge>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <Alert variant="success">
                        <FaCheckCircle className="me-2" />
                        No risk alerts identified for this project.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}
            </div>
          </Card.Body>

          <Card.Footer className="bg-white">
            <Row>
              <Col>
                <Button
                  variant="outline-secondary"
                  as={Link}
                  to={`/rnd/projects/${id}`}
                >
                  <FaArrowLeft className="me-1" /> Detail Proyek
                </Button>
              </Col>
              <Col className="text-end">
                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" /> Simpan Progress
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Card.Footer>
        </Card>
      </Container>

      {/* Modal for adding a new attachment */}
      <Modal 
        show={showAddAttachmentModal} 
        onHide={() => setShowAddAttachmentModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload New File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>File</Form.Label>
              <FileUploader 
                onFileSelect={(file) => setNewAttachment({...newAttachment, file})}
              />
              {newAttachment.file && (
                <small className="text-success d-block mt-2">
                  Selected: {newAttachment.file.name} ({Math.round(newAttachment.file.size / 1024)} KB)
                </small>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={newAttachment.description}
                onChange={(e) => setNewAttachment({...newAttachment, description: e.target.value})}
                placeholder="Enter a brief description of this file"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddAttachmentModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddAttachment}
            disabled={!newAttachment.file}
          >
            Upload File
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for adding a new task */}
      <Modal 
        show={showAddTaskModal} 
        onHide={() => setShowAddTaskModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Task Title</Form.Label>
              <Form.Control 
                type="text" 
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                placeholder="Enter task title"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Assigned To</Form.Label>
              <Form.Control 
                type="text" 
                value={newTask.assignee}
                onChange={(e) => setNewTask({...newTask, assignee: e.target.value})}
                placeholder="Enter assignee name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control 
                type="date" 
                value={newTask.dueDate}
                onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={newTask.status}
                onChange={(e) => setNewTask({...newTask, status: e.target.value})}
              >
                <option value="pending">Pending</option>
                <option value="inprogress">In Progress</option>
                <option value="completed">Completed</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Enter task description"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddTaskModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddTask}
            disabled={!newTask.title || !newTask.dueDate}
          >
            Add Task
          </Button>
        </Modal.Footer>
      </Modal>
    </MainLayout>
  );
};

export default ProjectProgress;